import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="effective-date">Effective Date: October 26, 2025</p>

        <section>
          <h2>Introduction</h2>
          <p>
            Welcome to Simple Baby Sleep. We respect your privacy and are committed to protecting
            your personal data. This privacy policy explains how we collect, use, store, and
            protect your information when you use our baby sleep tracking application.
          </p>
        </section>

        <section>
          <h2>Data We Collect</h2>
          <p>When you use Simple Baby Sleep, we collect the following information:</p>
          <ul>
            <li><strong>Account Information:</strong> Email address and username</li>
            <li><strong>Baby Information:</strong> Baby's name and birthdate</li>
            <li><strong>Sleep Tracking Data:</strong> Sleep start times, end times, and durations</li>
            <li><strong>Usage Data:</strong> App preferences and settings</li>
          </ul>
        </section>

        <section>
          <h2>How We Use Your Data</h2>
          <p>We use your personal data for the following purposes:</p>
          <ul>
            <li><strong>Service Provision:</strong> To provide sleep tracking functionality and display your data</li>
            <li><strong>Account Management:</strong> To manage your account and authenticate access</li>
            <li><strong>Email Notifications:</strong> To send verification codes and account-related communications</li>
            <li><strong>Service Improvement:</strong> To understand how our app is used and improve our service</li>
          </ul>
          <p>
            We process your data based on your consent (when you create an account) and our
            legitimate interest in providing and improving our service.
          </p>
        </section>

        <section>
          <h2>Data Storage and Security</h2>
          <p>
            Your data is stored securely on our servers using industry-standard encryption and
            security practices. We implement appropriate technical and organizational measures
            to protect your personal data against unauthorized access, alteration, disclosure,
            or destruction.
          </p>
          <p>
            We retain your data for as long as your account is active or as needed to provide
            you our services. If you delete your account, all your personal data will be
            permanently removed from our systems.
          </p>
        </section>

        <section>
          <h2>Your Rights Under GDPR</h2>
          <p>If you are in the European Economic Area (EEA), you have the following rights:</p>
          <ul>
            <li>
              <strong>Right to Access:</strong> You can view and access all your data within the app
            </li>
            <li>
              <strong>Right to Rectification:</strong> You can edit and update your information at any time
            </li>
            <li>
              <strong>Right to Deletion:</strong> You can delete your account and all associated data
              from the Settings page
            </li>
            <li>
              <strong>Right to Data Portability:</strong> You can export your data in a machine-readable
              format (contact us at hello@simplebabysleep.com)
            </li>
            <li>
              <strong>Right to Object:</strong> You can object to processing of your personal data
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> You can withdraw consent at any time by
              deleting your account
            </li>
          </ul>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <p>We use the following third-party services:</p>
          <ul>
            <li>
              <strong>Email Service Provider:</strong> We use Postmark to send verification codes
              and account-related emails. Your email address is shared with Postmark only for
              this purpose.
            </li>
            <li>
              <strong>Hosting Provider:</strong> Our servers are hosted securely with appropriate
              data protection measures in place.
            </li>
          </ul>
          <p>
            We do not sell, rent, or share your personal data with third parties for marketing
            purposes. We never share your baby's information or sleep tracking data with anyone
            unless you explicitly share access with other users.
          </p>
        </section>

        <section>
          <h2>Cookies and Tracking</h2>
          <p>
            We use minimal tracking technologies. We store your authentication session in your
            browser's local storage to keep you logged in. We do not use third-party analytics
            or advertising cookies.
          </p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>
            This app is designed for parents to track their babies' sleep patterns. We do not
            knowingly collect personal information directly from children. The app should only
            be used by adults (parents or guardians) who are 18 years or older.
          </p>
        </section>

        <section>
          <h2>Data Sharing Between Users</h2>
          <p>
            You can choose to share access to your baby's sleep tracking data with other users
            via invite codes. When you share access, the other user will be able to view and
            edit sleep entries for that baby. You can revoke access at any time from the
            Settings page.
          </p>
        </section>

        <section>
          <h2>International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries other than your country
            of residence. We ensure that appropriate safeguards are in place to protect your
            data in accordance with this privacy policy and applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this privacy policy from time to time. We will notify you of any
            significant changes by updating the "Effective Date" at the top of this policy.
            Your continued use of the app after any changes indicates your acceptance of the
            updated policy.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or how we handle your data,
            please contact us at:
          </p>
          <p className="contact-email">
            <a href="mailto:hello@simplebabysleep.com">hello@simplebabysleep.com</a>
          </p>
          <p>
            If you are in the EEA and believe we have not addressed your concerns adequately,
            you have the right to lodge a complaint with your local data protection authority.
          </p>
        </section>

        <div className="privacy-footer">
          <Link to="/welcome" className="back-link">← Back to Home</Link>
          <Link to="/support" className="support-link">Support & About</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
